import { getAuthToken } from "@store/auth/cookieStorage";
import { dev } from "@utils/dev";
import { REACT_APP_BASE_URL } from "../../../config";
import { SocketEventTypes, SocketMessage } from "./responses";

const TAG = "WmSocket";

const HTTPS_PREFIX = "https://";
const HTTP_PREFIX = "http://";
const PING = "ping";
const PONG = "pong"
const PING_PERIOD_MS = 60000; // 1min
const PONG_TIMEOUT_MS = 15000; // 15sec
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

function getBaseUrl(): string {
  let result = REACT_APP_BASE_URL;
  if (!result) {
    result = window.location.origin;
  }
  return result;
}

function getWsUrl(token: string): string {
  const baseUrl = getBaseUrl();
  let idx = baseUrl.toLowerCase().indexOf(HTTPS_PREFIX);
  if (idx > -1) {
    return `wss://${baseUrl.substring(HTTPS_PREFIX.length)}/api/socket?token=${token}`;
  }
  return `ws://${baseUrl.substring(HTTP_PREFIX.length)}/api/socket?token=${token}`;
}

export enum SocketStatus {
  NEED_LOGON = 0,
  ON = 1,
  OFF = 2,
  RECONNECTING = 3,
}

type OnMessageListener<T> = (message: SocketMessage<T>) => void;
type StatusChangeListener = (status: SocketStatus) => void;

interface SocketListener {
  onMessage?: OnMessageListener<any>;
  onReconnected?: () => void;
  onStatusChanged?: StatusChangeListener;
}

function getReconectDelay(reconnect: number): number {
  const attempt = Math.min(reconnect, 5);
  const expDelay = RECONNECT_BASE_DELAY_MS * 2 ** attempt;
  const jitter = Math.random() * RECONNECT_BASE_DELAY_MS;
  return Math.min(expDelay + jitter, RECONNECT_MAX_DELAY_MS);
}

class WmSocket {

  private state: SocketStatus = SocketStatus.OFF;
  private pingTimer: ReturnType<typeof setTimeout> | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private socket?: WebSocket;
  private reconnect = 0;

  private sendBuffer: string[] = [];
  private readonly maxSendBufferSize = 100;


  private _onMessage?: OnMessageListener<any>;
  private _onReconnected?: () => void;
  private _onStatusChanged?: StatusChangeListener;

  start(listener: SocketListener) {
    const token = getAuthToken();
    if (token && token !== "null") {
      if (this.socket) {
        try {
          this.socket.close();
          this.socket = undefined;
        } catch { }
      }
      this._onMessage = listener.onMessage?.bind(this);
      this._onReconnected = listener.onReconnected?.bind(this);
      this._onStatusChanged = listener.onStatusChanged?.bind(this);
      const socket = new WebSocket(getWsUrl(token));
      this.socket = socket;
      this.socket.onopen = () => this.onOpen(socket);
      this.socket.onmessage = (event) => this.onMessage(event, socket);
      this.socket.onerror = (event) => this.onError(event, socket);
      this.socket.onclose = (event) => this.onClose(event, socket);
      this.state = SocketStatus.ON;
      this.startPing(socket);
    } else {
      this.state = SocketStatus.OFF;
      dev.logt(TAG, "no token");
    }
  }

  private startPing(socket: WebSocket) {
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
    }
    this.pingTimer = setTimeout(() => {
      this.ping(socket);
    }, PING_PERIOD_MS);
  }

  private ping(socket: WebSocket) {
    if (this.socket !== socket) return;
    if (this.socket) {
      this.socket.send(PING);
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
      }
      this.timeoutTimer = setTimeout(() => {
        this.doTimeout(socket);
      }, PONG_TIMEOUT_MS);
    }
  }

  private onPong(socket: WebSocket) {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
    this.startPing(socket);
  }

  private doTimeout(socket: WebSocket) {
    if (this.socket !== socket) return;
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
    this.doReconnect();
  }

  private doReconnect() {
    const socket = this.socket;
    this.socket = undefined;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    setTimeout(() => {
      this.start({
        onMessage: this._onMessage,
        onReconnected: this._onReconnected,
        onStatusChanged: this._onStatusChanged
      });
    }, 100)
  }

  private onOpen(socket: WebSocket) {
    if (this.socket !== socket) return;
    if (this.reconnect > 0) {
      this._onReconnected?.();
      this.reconnect = 0;
    }
    this._onStatusChanged?.(SocketStatus.ON);
    this.flushSendBuffer(socket);
    console.log(`${TAG} onOpen event`);
  }

  private serializeMessage(message: any): string {
    return typeof message === "string" ? message : JSON.stringify(message);
  }

  private enqueueMessage(message: string) {
    if (this.sendBuffer.length >= this.maxSendBufferSize) {
      this.sendBuffer.shift();
      dev.logt(TAG, "send buffer overflow, dropped oldest message");
    }
    this.sendBuffer.push(message);
  }

  private flushSendBuffer(socket: WebSocket | undefined = this.socket) {
    if (!socket || socket !== this.socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    while (
      this.sendBuffer.length > 0 &&
      socket === this.socket &&
      socket.readyState === WebSocket.OPEN
    ) {
      const message = this.sendBuffer.shift();
      if (!message) {
        continue;
      }
      try {
        socket.send(message);
      } catch (e) {
        this.sendBuffer.unshift(message);
        dev.logt(TAG, "flushSendBuffer failed", e);
        return;
      }
    }
  }


  sendOrEnqueue(message: any) {
    const payload = this.serializeMessage(message);
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      this.enqueueMessage(payload);
      return;
    }
    if (this.sendBuffer.length > 0) {
      this.enqueueMessage(payload);
      this.flushSendBuffer(socket);
      return;
    }
    try {
      socket.send(payload);
    } catch (e) {
      this.enqueueMessage(payload);
      dev.logt(TAG, "sendIfReady failed, message buffered", e);
    }
  }

  // private sendIfReady(message: any, retries: number = 0) {
  //   if (this.socket) {
  //     if (this.socket.readyState === WebSocket.OPEN) {
  //       this.socket.send(message);
  //     } else {
  //       if (this.socket.readyState === WebSocket.CLOSING || this.socket.readyState === WebSocket.CLOSED) {
  //         return;
  //       }
  //       if (retries < 4) {
  //         setTimeout(() => {
  //           this.sendIfReady(message, retries + 1);
  //         }, 100);
  //       } else {
  //         dev.logt(TAG, "sendIfReady failed after 4 retries");
  //       }
  //     }
  //   }
  // }

  // private async trySend(message: any, retries: number = 0) {
  //   try {
  //     this.socket?.send(message);
  //   } catch (e) {
  //     if (retries < 4) {
  //       // await this.waitForConnection();
  //       this.trySend(message, retries + 1);
  //     } else throw e;
  //   }
  // }

  // send(message: any) {
  //   this.sendIfReady(message);
  //   if (this.socket) {
  //     if (typeof message === "string") {
  //       this.trySend(message);
  //     } else {
  //       this.trySend(JSON.stringify(message));
  //     }
  //   }
  // }

  private onMessage(e: MessageEvent, socket: WebSocket) {
    if (this.socket !== socket) return;
    try {
      if (e.data === PING) {
        this.socket.send(PONG);
        return;
      }
      if (e.data === PONG) {
        this.onPong(socket);
        return;
      }
      const message = JSON.parse(e.data) as SocketMessage<any>;
      if (!message.type && message.status) {
        return;
      }
      if (message.type === SocketEventTypes.NEED_LOGON || message.code === 403) {
        this._onStatusChanged?.(SocketStatus.NEED_LOGON);
        return;
      }
      this._onMessage?.(message);
    } catch (e) {
      dev.logt("WS", "onMessage error", e);
    }
  }

  private onError(e: Event, socket: WebSocket) {
    if (this.socket !== socket) return;
    console.log(`${TAG} onError event`, e);
  }

  private onClose(e: CloseEvent, socket: WebSocket) {
    if (this.socket !== socket) return;
    console.log(`${TAG} onClose event`, e);
    if (this.state === SocketStatus.ON) { // restart
      this._onStatusChanged?.(SocketStatus.RECONNECTING);
      const delay = getReconectDelay(this.reconnect);
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (this.socket !== socket) return;
        this.reconnect += 1;
        console.log(`${TAG} reconnecting (delay: ${delay}, attempt: ${this.reconnect})`, e);
        this.start({
          onMessage: this._onMessage,
          onReconnected: this._onReconnected,
          onStatusChanged: this._onStatusChanged
        });
      }, delay);
    } else {
      this._onStatusChanged?.(SocketStatus.OFF);
    }
  }

  close() {
    console.log(`${TAG} do close`);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    this.sendBuffer = [];
    const socket = this.socket;
    this.socket = undefined;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    this.state = SocketStatus.OFF;
  }
}

const ws = new WmSocket();
export default ws;